import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  getEmpresa, 
  getEjercicios, 
  exportarEjercicio, 
  exportarBalance,
  exportarPygDespacho,
  exportarEbitda,
  exportarFlujoCaja,
  exportarFlujoCajaDetallado,
  exportarComparativa,
  exportarComparativaFlujos,
  exportarComparativaFlujosDetallado,
  exportarDetallado,
  exportarDetalladoMax,
  exportarPlantilla,
  exportarProyeccion,
  exportarDriversEmpresa
} from "@/lib/api";
import { downloadBlob, parseBlobError } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Download, 
  FileSpreadsheet, 
  BarChart3, 
  PieChart,
  TrendingUp,
  GitCompare,
  ArrowLeft,
  FileText,
  Calculator,
  Banknote,
  FileSearch,
  Map
} from "lucide-react";

export default function ExportacionesPage() {
  const { empresaId } = useParams();
  const navigate = useNavigate();
  const [empresa, setEmpresa] = useState(null);
  const [ejercicios, setEjercicios] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estado para exportaciones individuales
  const [selectedEjercicio, setSelectedEjercicio] = useState("");
  const [exportingDashboard, setExportingDashboard] = useState(false);
  const [exportingBalanceStd, setExportingBalanceStd] = useState(false);
  const [exportingBalance, setExportingBalance] = useState(false);
  const [exportingBalanceMax, setExportingBalanceMax] = useState(false);
  const [exportingPyg, setExportingPyg] = useState(false);
  const [exportingEbitda, setExportingEbitda] = useState(false);
  const [exportingFlujoCaja, setExportingFlujoCaja] = useState(false);
  const [exportingFlujoCajaDetallado, setExportingFlujoCajaDetallado] = useState(false);
  
  // Estado para exportaciones comparativas
  const [selectedAnios, setSelectedAnios] = useState([]);
  const [exportingComparativa, setExportingComparativa] = useState(false);
  const [exportingComparativaFlujos, setExportingComparativaFlujos] = useState(false);
  const [exportingComparativaFlujosDetallado, setExportingComparativaFlujosDetallado] = useState(false);
  const [exportingDetallado, setExportingDetallado] = useState(false);
  const [exportingDetalladoMax, setExportingDetalladoMax] = useState(false);
  const [exportingPlantilla, setExportingPlantilla] = useState(false);
  const [exportingProyeccion, setExportingProyeccion] = useState(false);
  const [exportingDrivers, setExportingDrivers] = useState(false);

  useEffect(() => {
    loadData();
  }, [empresaId]);

  const loadData = async () => {
    try {
      const [empresaRes, ejerciciosRes] = await Promise.all([
        getEmpresa(empresaId),
        getEjercicios(empresaId),
      ]);
      setEmpresa(empresaRes.data);
      const ejerciciosConDatos = ejerciciosRes.data
        .filter((e) => e.total_cuentas > 0)
        .sort((a, b) => b.anio - a.anio);
      setEjercicios(ejerciciosConDatos);
      
      // Auto-seleccionar el ejercicio más reciente
      if (ejerciciosConDatos.length > 0) {
        setSelectedEjercicio(ejerciciosConDatos[0].id);
      }
      
      // Auto-seleccionar todos los años para comparativas
      const anios = ejerciciosConDatos.map((e) => e.anio).sort();
      setSelectedAnios(anios);
    } catch (error) {
      toast.error("Error al cargar datos");
      navigate("/empresas");
    } finally {
      setLoading(false);
    }
  };

  const getEjercicioAnio = (id) => {
    const ej = ejercicios.find(e => e.id === id);
    return ej?.anio || "";
  };

  const tieneAnioAnterior = (id) => {
    const anio = getEjercicioAnio(id);
    return ejercicios.some(e => e.anio === anio - 1 && e.total_cuentas > 0);
  };

  const toggleAnio = (anio) => {
    setSelectedAnios((prev) =>
      prev.includes(anio) ? prev.filter((a) => a !== anio) : [...prev, anio].sort()
    );
  };

  // Exportaciones individuales
  const handleExportDashboard = async () => {
    if (!selectedEjercicio) return;
    setExportingDashboard(true);
    try {
      const response = await exportarEjercicio(selectedEjercicio);
      const anio = getEjercicioAnio(selectedEjercicio);
      downloadBlob(response.data, `Balances_Ratios_${empresa?.nombre?.replace(/\s+/g, '_')}_${anio}.xlsx`);
      toast.success("Balances y Ratios exportado correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar Balances y Ratios"));
    } finally {
      setExportingDashboard(false);
    }
  };

  const handleExportBalance = async () => {
    if (!selectedEjercicio) return;
    setExportingBalanceStd(true);
    try {
      const response = await exportarBalance(selectedEjercicio);
      const anio = getEjercicioAnio(selectedEjercicio);
      downloadBlob(response.data, `Balance_${empresa?.nombre?.replace(/\s+/g, '_')}_${anio}.xlsx`);
      toast.success("Balance exportado correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar el balance"));
    } finally {
      setExportingBalanceStd(false);
    }
  };

  const handleExportBalanceDetallado = async () => {
    if (!selectedEjercicio) return;
    setExportingBalance(true);
    try {
      const anio = getEjercicioAnio(selectedEjercicio);
      const response = await exportarDetallado(empresaId, anio.toString());
      downloadBlob(response.data, `Balance_3dig_${empresa?.nombre?.replace(/\s+/g, '_')}_${anio}.xlsx`);
      toast.success("Balance a 3 dígitos exportado correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar el balance a 3 dígitos"));
    } finally {
      setExportingBalance(false);
    }
  };

  const handleExportBalanceMax = async () => {
    if (!selectedEjercicio) return;
    setExportingBalanceMax(true);
    try {
      const anio = getEjercicioAnio(selectedEjercicio);
      const response = await exportarDetalladoMax(empresaId, anio.toString());
      downloadBlob(response.data, `Balance_MaxDetalle_${empresa?.nombre?.replace(/\s+/g, '_')}_${anio}.xlsx`);
      toast.success("Balance con máximo detalle exportado correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar el balance con máximo detalle"));
    } finally {
      setExportingBalanceMax(false);
    }
  };

  const handleExportPyg = async () => {
    if (!selectedEjercicio) return;
    setExportingPyg(true);
    try {
      const response = await exportarPygDespacho(selectedEjercicio);
      const anio = getEjercicioAnio(selectedEjercicio);
      downloadBlob(response.data, `PyG_Despacho_${empresa?.nombre?.replace(/\s+/g, '_')}_${anio}.xlsx`);
      toast.success("PyG exportado correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar la PyG"));
    } finally {
      setExportingPyg(false);
    }
  };

  const handleExportEbitda = async () => {
    if (!selectedEjercicio) return;
    setExportingEbitda(true);
    try {
      const response = await exportarEbitda(selectedEjercicio);
      const anio = getEjercicioAnio(selectedEjercicio);
      downloadBlob(response.data, `EBITDA_${empresa?.nombre?.replace(/\s+/g, '_')}_${anio}.xlsx`);
      toast.success("EBITDA exportado correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar el EBITDA"));
    } finally {
      setExportingEbitda(false);
    }
  };

  const handleExportFlujoCaja = async () => {
    if (!selectedEjercicio) return;
    const anio = getEjercicioAnio(selectedEjercicio);
    const tieneAnioAnterior = ejercicios.some(e => e.anio === anio - 1 && e.total_cuentas > 0);
    if (!tieneAnioAnterior) {
      toast.error(`No hay datos del ejercicio ${anio - 1} para calcular el flujo de caja`);
      return;
    }
    setExportingFlujoCaja(true);
    try {
      const response = await exportarFlujoCaja(selectedEjercicio);
      downloadBlob(response.data, `FlujoCaja_${empresa?.nombre?.replace(/\s+/g, '_')}_${anio}.xlsx`);
      toast.success("Flujo de Caja exportado correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar el Flujo de Caja"));
    } finally {
      setExportingFlujoCaja(false);
    }
  };

  const handleExportFlujoCajaDetallado = async () => {
    if (!selectedEjercicio) return;
    const anio = getEjercicioAnio(selectedEjercicio);
    const tieneAnioAnterior = ejercicios.some(e => e.anio === anio - 1 && e.total_cuentas > 0);
    if (!tieneAnioAnterior) {
      toast.error(`No hay datos del ejercicio ${anio - 1} para calcular el flujo de caja`);
      return;
    }
    setExportingFlujoCajaDetallado(true);
    try {
      const response = await exportarFlujoCajaDetallado(selectedEjercicio);
      downloadBlob(response.data, `FlujoCaja_Detallado_${empresa?.nombre?.replace(/\s+/g, '_')}_${anio}.xlsx`);
      toast.success("Flujo de Caja Detallado exportado correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar el Flujo de Caja Detallado"));
    } finally {
      setExportingFlujoCajaDetallado(false);
    }
  };

  // Exportaciones comparativas
  const handleExportComparativa = async () => {
    if (selectedAnios.length < 2) {
      toast.error("Selecciona al menos 2 años para la comparativa");
      return;
    }
    setExportingComparativa(true);
    try {
      const response = await exportarComparativa(empresaId, selectedAnios.join(","));
      downloadBlob(response.data, `Comparativa_${empresa?.nombre?.replace(/\s+/g, '_')}_${selectedAnios.join('-')}.xlsx`);
      toast.success("Comparativa exportada correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar la comparativa"));
    } finally {
      setExportingComparativa(false);
    }
  };

  const handleExportComparativaFlujos = async () => {
    // Filtrar años que tienen año anterior disponible
    const aniosConAnterior = selectedAnios.filter(anio => 
      ejercicios.some(e => e.anio === anio - 1 && e.total_cuentas > 0)
    );
    if (aniosConAnterior.length < 1) {
      toast.error("Ningún año seleccionado tiene datos del año anterior para calcular flujos");
      return;
    }
    setExportingComparativaFlujos(true);
    try {
      const response = await exportarComparativaFlujos(empresaId, aniosConAnterior.join(","));
      downloadBlob(response.data, `Comparativa_Flujos_${empresa?.nombre?.replace(/\s+/g, '_')}_${aniosConAnterior.join('-')}.xlsx`);
      toast.success("Comparativa de Flujos exportada correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar la comparativa de flujos"));
    } finally {
      setExportingComparativaFlujos(false);
    }
  };

  const handleExportComparativaFlujosDetallado = async () => {
    const aniosConAnterior = selectedAnios.filter(anio => 
      ejercicios.some(e => e.anio === anio - 1 && e.total_cuentas > 0)
    );
    if (aniosConAnterior.length < 1) {
      toast.error("Ningún año seleccionado tiene datos del año anterior para calcular flujos");
      return;
    }
    setExportingComparativaFlujosDetallado(true);
    try {
      const response = await exportarComparativaFlujosDetallado(empresaId, aniosConAnterior.join(","));
      downloadBlob(response.data, `EFC_Detallado_${empresa?.nombre?.replace(/\s+/g, '_')}_${aniosConAnterior.join('-')}.xlsx`);
      toast.success("Flujos de Caja con máximo detalle exportado correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar los flujos detallados"));
    } finally {
      setExportingComparativaFlujosDetallado(false);
    }
  };

  const handleExportDetallado = async () => {
    if (selectedAnios.length === 0) {
      toast.error("Selecciona al menos un año");
      return;
    }
    setExportingDetallado(true);
    try {
      const response = await exportarDetallado(empresaId, selectedAnios.join(","));
      downloadBlob(response.data, `Detallado_3dig_${empresa?.nombre?.replace(/\s+/g, '_')}_${selectedAnios.join('-')}.xlsx`);
      toast.success("Exportación detallada completada");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar el detallado"));
    } finally {
      setExportingDetallado(false);
    }
  };

  const handleExportDetalladoMax = async () => {
    if (selectedAnios.length === 0) {
      toast.error("Selecciona al menos un año");
      return;
    }
    setExportingDetalladoMax(true);
    try {
      const response = await exportarDetalladoMax(empresaId, selectedAnios.join(","));
      downloadBlob(response.data, `MaxDetalle_${empresa?.nombre?.replace(/\s+/g, '_')}_${selectedAnios.join('-')}.xlsx`);
      toast.success("Exportación con máximo detalle completada");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar con máximo detalle"));
    } finally {
      setExportingDetalladoMax(false);
    }
  };

  const handleExportPlantilla = async () => {
    if (selectedAnios.length === 0) {
      toast.error("Selecciona al menos un año");
      return;
    }
    setExportingPlantilla(true);
    try {
      const response = await exportarPlantilla(empresaId, selectedAnios.join(","));
      downloadBlob(response.data, `Plantilla_PdV_${empresa?.nombre?.replace(/\s+/g, '_')}_${selectedAnios.join('-')}.xlsx`);
      toast.success("Plantilla exportada correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar la plantilla"));
    } finally {
      setExportingPlantilla(false);
    }
  };

  const handleExportProyeccion = async () => {
    setExportingProyeccion(true);
    try {
      const response = await exportarProyeccion(empresaId);
      downloadBlob(response.data, `Proyeccion_${empresa?.nombre?.replace(/\s+/g, '_')}.xlsx`);
      toast.success("Proyección exportada correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar la proyección"));
    } finally {
      setExportingProyeccion(false);
    }
  };

  // Mapa de hipótesis: todos los drivers aplicados, con estado y fecha (30-09)
  const handleExportDrivers = async () => {
    setExportingDrivers(true);
    try {
      const response = await exportarDriversEmpresa(empresaId);
      downloadBlob(response.data, `Drivers_${empresa?.nombre?.replace(/\s+/g, '_')}.xlsx`);
      toast.success("Drivers exportados correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudieron exportar los drivers"));
    } finally {
      setExportingDrivers(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto" data-testid="exportaciones-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/empresas/${empresaId}`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="page-title">Centro de Exportaciones</h1>
          <p className="text-muted-foreground text-sm">{empresa?.nombre}</p>
        </div>
      </div>

      {/* Exportaciones de Ejercicio Individual */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="h-5 w-5 text-blue-500" />
            <div>
              <CardTitle>Exportaciones por Ejercicio</CardTitle>
              <CardDescription>Exporta informes de un ejercicio específico</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Selector de Ejercicio */}
          <div className="space-y-2">
            <Label>Seleccionar Ejercicio</Label>
            <Select value={selectedEjercicio} onValueChange={setSelectedEjercicio}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="ejercicio-selector">
                <SelectValue placeholder="Seleccionar año" />
              </SelectTrigger>
              <SelectContent>
                {ejercicios.map((ej) => (
                  <SelectItem key={ej.id} value={ej.id}>
                    Ejercicio {ej.anio}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Botones de exportación individual */}
          <div className="grid gap-3 sm:grid-cols-2">
            <ExportButton
              icon={PieChart}
              title="Balances y Ratios"
              description="Balance, PyG y ratios financieros"
              onClick={handleExportDashboard}
              loading={exportingDashboard}
              disabled={!selectedEjercicio}
              testId="export-dashboard-btn"
            />
            <ExportButton
              icon={FileText}
              title="Balance (estándar)"
              description="Balance de Situación en formato estándar"
              onClick={handleExportBalance}
              loading={exportingBalanceStd}
              disabled={!selectedEjercicio}
              testId="export-balance-std-btn"
            />
            <ExportButton
              icon={FileText}
              title="Balances a 3 dígitos"
              description="Balance, PyG y ratios con cuentas agrupadas a 3 dígitos"
              onClick={handleExportBalanceDetallado}
              loading={exportingBalance}
              disabled={!selectedEjercicio}
              testId="export-balance-btn"
            />
            <ExportButton
              icon={FileSearch}
              title="Balances a máximos dígitos"
              description="Desglose de cuentas agrupadas a máximo dígitos"
              onClick={handleExportBalanceMax}
              loading={exportingBalanceMax}
              disabled={!selectedEjercicio}
              testId="export-balance-max-btn"
            />
            <ExportButton
              icon={BarChart3}
              title="PyG Formato Despacho"
              description="Cuenta de Pérdidas y Ganancias en formato profesional"
              onClick={handleExportPyg}
              loading={exportingPyg}
              disabled={!selectedEjercicio}
              testId="export-pyg-btn"
            />
            <ExportButton
              icon={TrendingUp}
              title="EBITDA Ajustado"
              description="Cálculo de EBITDA con ajustes y desglose"
              onClick={handleExportEbitda}
              loading={exportingEbitda}
              disabled={!selectedEjercicio}
              testId="export-ebitda-btn"
            />
            <ExportButton
              icon={Banknote}
              title="Flujo de Caja"
              description="Estado de Flujos de Caja (CFO, CFI, CFF)"
              onClick={handleExportFlujoCaja}
              loading={exportingFlujoCaja}
              disabled={!selectedEjercicio || !tieneAnioAnterior(selectedEjercicio)}
              badge={selectedEjercicio && !tieneAnioAnterior(selectedEjercicio) ? "Sin año anterior" : null}
              testId="export-flujo-caja-btn"
            />
            <ExportButton
              icon={FileSearch}
              title="Flujo de Caja Detallado"
              description="EFC con fórmulas, valores t y t-1, y cálculo completo"
              onClick={handleExportFlujoCajaDetallado}
              loading={exportingFlujoCajaDetallado}
              disabled={!selectedEjercicio || !tieneAnioAnterior(selectedEjercicio)}
              badge={selectedEjercicio && !tieneAnioAnterior(selectedEjercicio) ? "Sin año anterior" : null}
              testId="export-flujo-caja-detallado-btn"
            />
          </div>
        </CardContent>
      </Card>

      {/* Exportaciones Comparativas Multi-año */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <GitCompare className="h-5 w-5 text-emerald-500" />
            <div>
              <CardTitle>Exportaciones Comparativas</CardTitle>
              <CardDescription>Exporta comparativas de varios ejercicios</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Selector de Años */}
          <div className="space-y-2">
            <Label>Seleccionar Ejercicios</Label>
            <div className="flex flex-wrap gap-3">
              {ejercicios.map((ej) => (
                <div key={ej.anio} className="flex items-center space-x-2">
                  <Checkbox
                    id={`compare-${ej.anio}`}
                    checked={selectedAnios.includes(ej.anio)}
                    onCheckedChange={() => toggleAnio(ej.anio)}
                  />
                  <Label htmlFor={`compare-${ej.anio}`} className="font-mono cursor-pointer">
                    {ej.anio}
                  </Label>
                </div>
              ))}
            </div>
            {selectedAnios.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {selectedAnios.length} año{selectedAnios.length !== 1 ? 's' : ''} seleccionado{selectedAnios.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          <Separator />

          {/* Botones de exportación comparativa */}
          <div className="grid gap-3 sm:grid-cols-2">
            <ExportButton
              icon={GitCompare}
              title="Balances y ratios mínimo dígitos"
              description="Tabla comparativa de KPIs y ratios entre años"
              onClick={handleExportComparativa}
              loading={exportingComparativa}
              disabled={selectedAnios.length < 2}
              badge={selectedAnios.length < 2 ? "Mín. 2 años" : null}
              testId="export-comparativa-btn"
            />
            <ExportButton
              icon={Banknote}
              title="Comparativa Flujos de Caja"
              description="Tabla comparativa de CFO, CFI, CFF entre años"
              onClick={handleExportComparativaFlujos}
              loading={exportingComparativaFlujos}
              disabled={selectedAnios.filter(anio => ejercicios.some(e => e.anio === anio - 1 && e.total_cuentas > 0)).length === 0}
              badge={selectedAnios.length > 0 && selectedAnios.filter(anio => ejercicios.some(e => e.anio === anio - 1 && e.total_cuentas > 0)).length === 0 ? "Sin años válidos" : null}
              testId="export-comparativa-flujos-btn"
            />
            <ExportButton
              icon={FileSearch}
              title="Flujos de Caja Detallado"
              description="EFC con valores t, t-1, variaciones y fórmulas"
              onClick={handleExportComparativaFlujosDetallado}
              loading={exportingComparativaFlujosDetallado}
              disabled={selectedAnios.filter(anio => ejercicios.some(e => e.anio === anio - 1 && e.total_cuentas > 0)).length === 0}
              badge={selectedAnios.length > 0 && selectedAnios.filter(anio => ejercicios.some(e => e.anio === anio - 1 && e.total_cuentas > 0)).length === 0 ? "Sin años válidos" : null}
              testId="export-comparativa-flujos-detallado-btn"
            />
            <ExportButton
              icon={Calculator}
              title="Balances a 3 dígitos"
              description="Desglose de cuentas agrupadas por los primeros 3 dígitos"
              onClick={handleExportDetallado}
              loading={exportingDetallado}
              disabled={selectedAnios.length === 0}
              testId="export-detallado-btn"
            />
            <ExportButton
              icon={FileSearch}
              title="Balances a máximos dígitos"
              description="Desglose de cuentas agrupadas a máximo dígitos"
              onClick={handleExportDetalladoMax}
              loading={exportingDetalladoMax}
              disabled={selectedAnios.length === 0}
              testId="export-detallado-max-btn"
            />
            <ExportButton
              icon={FileSpreadsheet}
              title="Plantilla PdV"
              description="Formato para importación en otras aplicaciones"
              onClick={handleExportPlantilla}
              loading={exportingPlantilla}
              disabled={selectedAnios.length === 0}
              testId="export-plantilla-btn"
            />
            <ExportButton
              icon={TrendingUp}
              title="Proyección financiera"
              description="PyG, balance y flujo proyectados con drivers, financiación y escenarios"
              onClick={handleExportProyeccion}
              loading={exportingProyeccion}
              disabled={ejercicios.length === 0}
              badge={ejercicios.length === 0 ? "Sin ejercicios" : null}
              testId="export-proyeccion-btn"
            />
            <ExportButton
              icon={Map}
              title="Drivers aplicados"
              description="Mapa de hipótesis: cada driver con su hipótesis, estado y fecha, más operaciones y parámetros"
              onClick={handleExportDrivers}
              loading={exportingDrivers}
              disabled={ejercicios.length === 0}
              badge={ejercicios.length === 0 ? "Sin ejercicios" : null}
              testId="export-drivers-btn"
            />
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="bg-muted/30">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Download className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Sobre las exportaciones</p>
              <p>Todos los informes se exportan en formato Excel (.xlsx) compatible con Microsoft Excel, Google Sheets y otras aplicaciones de hojas de cálculo.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ExportButton({ icon: Icon, title, description, onClick, loading, disabled, badge, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={testId}
      className="flex items-start gap-3 p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <div className="p-2 rounded-md bg-primary/10">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium">{title}</span>
          {badge && (
            <Badge variant="secondary" className="text-xs">{badge}</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <div className="shrink-0">
        {loading ? (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
        ) : (
          <Download className="h-5 w-5 text-muted-foreground" />
        )}
      </div>
    </button>
  );
}
